import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Truck, ArrowUpFromLine, ArrowDownToLine, Check, RefreshCw, MapPin, Clock } from 'lucide-react';
import { PIEZAS_TRASLADO as PIEZAS } from '../constants/productos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP = (allowHalf) => allowHalf ? 0.5 : 1;
const parseQty = (val, allowHalf) => {
  if (allowHalf) return Math.round((parseFloat(val) || 0) * 2) / 2;
  return parseInt(val) || 0;
};
const fmtQty = (qty) => qty % 1 !== 0 ? qty.toFixed(1) : String(qty);

function TransferCard({ traslado, tipo, onConfirm, submitting, disponible }) {
  const [reales, setReales] = useState({});

  useEffect(() => {
    // Pre-fill with scheduled/sent quantities
    const source = tipo === 'salida'
      ? traslado.productos
      : (traslado.productos_reales_salida || traslado.productos);
    const init = {};
    PIEZAS.forEach(p => {
      const qty = source?.[p.clave] || 0;
      if (qty > 0) init[p.clave] = qty;
    });
    setReales(init);
  }, [traslado, tipo]);

  const programados = tipo === 'salida'
    ? traslado.productos
    : (traslado.productos_reales_salida || traslado.productos);

  const hasAny = PIEZAS.some(p => (programados?.[p.clave] || 0) > 0);
  if (!hasAny) return null;

  const handleChange = (clave, value, allowHalf) => {
    const num = Math.max(0, parseQty(value, allowHalf));
    setReales(prev => ({ ...prev, [clave]: num }));
  };

  const handleIncrement = (clave, allowHalf) => {
    const step = STEP(allowHalf);
    setReales(prev => ({ ...prev, [clave]: Math.round(((prev[clave] || 0) + step) * 2) / 2 }));
  };

  const handleDecrement = (clave, allowHalf) => {
    const step = STEP(allowHalf);
    setReales(prev => ({ ...prev, [clave]: Math.max(0, Math.round(((prev[clave] || 0) - step) * 2) / 2) }));
  };

  // For salidas: check if any real quantity exceeds available stock
  const excededProducts = tipo === 'salida' && disponible
    ? PIEZAS.filter(p => (reales[p.clave] || 0) > (disponible[p.clave] ?? Infinity))
    : [];
  const hasStockError = excededProducts.length > 0;

  const destLabel = tipo === 'salida'
    ? `→ ${traslado.destino_nombre}`
    : `← ${traslado.origen_nombre}`;

  const bgHeader = tipo === 'salida' ? 'bg-orange-50' : 'bg-blue-50';
  const borderColor = tipo === 'salida' ? 'border-orange-200' : 'border-blue-200';
  const btnBg = tipo === 'salida' ? 'bg-orange-600 active:bg-orange-700' : 'bg-blue-600 active:bg-blue-700';
  const iconColor = tipo === 'salida' ? 'text-orange-600' : 'text-blue-600';

  return (
    <div className={`bg-white rounded-xl border ${borderColor} overflow-hidden`} data-testid={`transfer-card-${tipo}-${traslado.id}`}>
      {/* Header */}
      <div className={`px-3 py-2 ${bgHeader} border-b ${borderColor} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          {tipo === 'salida'
            ? <ArrowUpFromLine className={`w-4 h-4 ${iconColor}`} />
            : <ArrowDownToLine className={`w-4 h-4 ${iconColor}`} />
          }
          <span className="font-bold text-sm text-zinc-800">
            {tipo === 'salida' ? 'SALIDA' : 'ENTRADA'}
          </span>
          <span className="text-sm text-zinc-500">{destLabel}</span>
          {traslado.repartidor_nombre && (
            <span className="text-xs text-zinc-400 ml-1">({traslado.repartidor_nombre})</span>
          )}
        </div>
      </div>

      {/* Compact Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500">
              <th className="text-left px-3 py-2 font-medium text-xs w-24">
                {tipo === 'salida' ? 'Programado' : 'Enviado'}
              </th>
              {PIEZAS.map(p => (
                <th key={p.clave} className="text-center px-1 py-2 font-medium text-xs w-14">{p.short}{p.allowHalf ? <span className="text-amber-500">½</span> : ''}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Row 1: Scheduled/Sent quantities (read-only) */}
            <tr className="border-b border-zinc-100">
              <td className="px-3 py-2 text-xs text-zinc-400 font-medium">
                {tipo === 'salida' ? 'Prog.' : 'Env.'}
              </td>
              {PIEZAS.map(p => {
                const qty = programados?.[p.clave] || 0;
                return (
                  <td key={p.clave} className={`text-center px-1 py-2 font-mono font-bold ${qty > 0 ? 'text-zinc-900' : 'text-zinc-300'}`}>
                    {qty > 0 ? fmtQty(qty) : '--'}
                  </td>
                );
              })}
            </tr>
            {/* Row 1b: Available stock (salida only) */}
            {tipo === 'salida' && disponible && (
              <tr className="border-b border-zinc-100 bg-green-50/40">
                <td className="px-3 py-2 text-xs text-green-700 font-medium">Exist.</td>
                {PIEZAS.map(p => {
                  const prog = programados?.[p.clave] || 0;
                  if (prog === 0) return <td key={p.clave} className="text-center px-1 py-2 text-zinc-300 font-mono text-xs">--</td>;
                  const disp = disponible[p.clave] ?? 0;
                  const isLow = disp < prog;
                  return (
                    <td key={p.clave} className={`text-center px-1 py-2 font-mono text-xs font-bold ${isLow ? 'text-red-600' : 'text-green-700'}`}>
                      {fmtQty(disp)}
                    </td>
                  );
                })}
              </tr>
            )}
            {/* Row 2: Actual quantities (editable) */}
            <tr className="bg-zinc-50/50">
              <td className="px-3 py-2 text-xs font-medium text-zinc-600">
                Real
              </td>
              {PIEZAS.map(p => {
                const prog = programados?.[p.clave] || 0;
                if (prog === 0) {
                  return <td key={p.clave} className="text-center px-1 py-2 text-zinc-300 font-mono">--</td>;
                }
                const real = reales[p.clave] || 0;
                const disp = disponible?.[p.clave] ?? Infinity;
                const overStock = tipo === 'salida' && real > disp;
                const diff = real !== prog;
                return (
                  <td key={p.clave} className="px-1 py-1">
                    <div className="flex flex-col items-center gap-0.5">
                      <button
                        onClick={() => handleIncrement(p.clave, p.allowHalf)}
                        className="w-10 h-5 flex items-center justify-center text-zinc-400 hover:text-zinc-700 rounded"
                        data-testid={`btn-inc-${tipo}-${traslado.id}-${p.clave}`}
                      >
                        <span className="text-xs font-bold">+</span>
                      </button>
                      <input
                        type="number"
                        inputMode={p.allowHalf ? 'decimal' : 'numeric'}
                        min="0"
                        step={p.allowHalf ? '0.5' : '1'}
                        value={real}
                        onChange={(e) => handleChange(p.clave, e.target.value, p.allowHalf)}
                        className={`w-full h-8 text-center border rounded text-sm font-mono font-bold focus:outline-none focus:ring-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                          overStock
                            ? 'border-red-500 text-red-700 bg-red-50 focus:ring-red-500'
                            : diff
                              ? 'border-red-300 text-red-600 bg-red-50 focus:ring-red-400'
                              : 'border-zinc-200 text-zinc-900 focus:ring-zinc-400'
                        }`}
                        data-testid={`input-real-${tipo}-${traslado.id}-${p.clave}`}
                      />
                      <button
                        onClick={() => handleDecrement(p.clave, p.allowHalf)}
                        className="w-10 h-5 flex items-center justify-center text-zinc-400 hover:text-zinc-700 rounded"
                        data-testid={`btn-dec-${tipo}-${traslado.id}-${p.clave}`}
                      >
                        <span className="text-xs font-bold">-</span>
                      </button>
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Stock error warning */}
      {hasStockError && (
        <div className="mx-3 mb-1 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium">
          La cantidad supera la existencia disponible en: {excededProducts.map(p => p.nombre).join(', ')}
        </div>
      )}

      {/* Confirm Button */}
      <div className="px-3 py-2 border-t border-zinc-100">
        <button
          onClick={() => onConfirm(traslado.id, reales)}
          disabled={submitting === traslado.id || hasStockError}
          className={`w-full h-11 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40 ${btnBg}`}
          data-testid={`btn-confirmar-${tipo}-${traslado.id}`}
        >
          {submitting === traslado.id ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4" />
              {tipo === 'salida' ? 'Confirmar Salida' : 'Confirmar Recepción'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export function TrasladosPOS() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const salidasRef = useRef(null);
  const entradasRef = useRef(null);
  const [data, setData] = useState({ salidas: [], entradas: [], historial: [], disponible: {} });
  const [misTraslados, setMisTraslados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const isRepartidor = user?.role === 'repartidor';

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      if (isRepartidor) {
        const { data: res } = await axios.get(`${API}/repartidor/mis-traslados`);
        setMisTraslados(res);
      }
      if (user?.punto_venta_id) {
        const { data: res } = await axios.get(`${API}/traslados-pos/${user.punto_venta_id}`);
        const desc = (a, b) => (b.programado_at || '').localeCompare(a.programado_at || '');
        const descHist = (a, b) => {
          const ta = a.entrada_confirmada_at || a.salida_confirmada_at || a.realizado_at || '';
          const tb = b.entrada_confirmada_at || b.salida_confirmada_at || b.realizado_at || '';
          return tb.localeCompare(ta);
        };
        setData({
          ...res,
          salidas:   [...(res.salidas   || [])].sort(desc),
          entradas:  [...(res.entradas  || [])].sort(desc),
          historial: [...(res.historial || [])].sort(descHist),
        });
      }
    } catch (err) {
      console.error('Error loading transfers:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.punto_venta_id, isRepartidor]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    if (!hash) return;
    const target = hash === '#entradas' ? entradasRef.current : hash === '#salidas' ? salidasRef.current : null;
    if (target) setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  }, [hash, data.salidas.length, data.entradas.length]);

  const handleConfirmarSalida = async (trasladoId, productosReales) => {
    setSubmitting(trasladoId);
    try {
      await axios.put(`${API}/traslados/${trasladoId}/confirmar-salida`, {
        productos_reales: productosReales,
      });
      await loadData(true);
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.detail || 'Error al confirmar salida');
    } finally {
      setSubmitting(null);
    }
  };

  const handleConfirmarEntrada = async (trasladoId, productosReales) => {
    setSubmitting(trasladoId);
    try {
      await axios.put(`${API}/traslados/${trasladoId}/confirmar-entrada`, {
        productos_reales: productosReales,
      });
      await loadData(true);
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.detail || 'Error al confirmar entrada');
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  const totalPending = data.salidas.length + data.entradas.length;

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    try {
      return new Date(isoStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const getStatusBadge = (status) => {
    if (status === 'procesado') return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Completado</span>;
    if (status === 'realizado') return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Realizado</span>;
    if (status === 'en_transito') return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">En tránsito</span>;
    return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Programado</span>;
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="traslados-pos-page">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/pos')} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg" data-testid="btn-back">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
                <Truck className="w-5 h-5" /> Traslados
              </h1>
              <p className="text-xs text-zinc-500">
                {isRepartidor
                  ? `${misTraslados.length} traslado${misTraslados.length !== 1 ? 's' : ''} asignado${misTraslados.length !== 1 ? 's' : ''}`
                  : totalPending > 0
                    ? `${totalPending} pendiente${totalPending > 1 ? 's' : ''}${data.historial.length > 0 ? ` · ${data.historial.length} completado${data.historial.length > 1 ? 's' : ''}` : ''}`
                    : data.historial.length > 0 ? `${data.historial.length} completado${data.historial.length > 1 ? 's' : ''} hoy` : 'Sin traslados hoy'}
              </p>
            </div>
          </div>
          <button onClick={() => loadData(true)} disabled={refreshing} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg" data-testid="btn-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex-1 px-3 py-4 space-y-4 overflow-auto">

        {/* ========== REPARTIDOR VIEW ========== */}
        {isRepartidor && misTraslados.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-base font-bold text-purple-700 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Mis Traslados ({misTraslados.length})
            </h2>
            {misTraslados.map(t => (
              <div key={t.id} className="bg-white rounded-xl border border-purple-200 overflow-hidden" data-testid={`repartidor-traslado-${t.id}`}>
                <div className="px-3 py-2 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-zinc-800">{t.origen_nombre}</span>
                    <span className="text-zinc-400">→</span>
                    <span className="text-sm font-medium text-zinc-800">{t.destino_nombre}</span>
                  </div>
                  {getStatusBadge(t.status)}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-zinc-50 text-zinc-500">
                        <th className="text-left px-3 py-1.5 font-medium text-xs w-20"></th>
                        {PIEZAS.map(p => (
                          <th key={p.clave} className="text-center px-1 py-1.5 font-medium text-xs w-10">{p.short}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-zinc-100">
                        <td className="px-3 py-1.5 text-xs text-zinc-400">Prog.</td>
                        {PIEZAS.map(p => {
                          const qty = t.productos?.[p.clave] || 0;
                          return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono ${qty > 0 ? 'font-bold text-zinc-900' : 'text-zinc-300'}`}>{qty > 0 ? fmtQty(qty) : '--'}</td>;
                        })}
                      </tr>
                      {t.productos_reales_salida && (
                        <tr className="border-b border-zinc-100 bg-orange-50/50">
                          <td className="px-3 py-1.5 text-xs text-orange-600">Salida</td>
                          {PIEZAS.map(p => {
                            const qty = t.productos_reales_salida?.[p.clave] || 0;
                            return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono ${qty > 0 ? 'font-bold text-orange-700' : 'text-zinc-300'}`}>{qty > 0 ? fmtQty(qty) : '--'}</td>;
                          })}
                        </tr>
                      )}
                      {t.productos_reales_entrada && (
                        <tr className="bg-blue-50/50">
                          <td className="px-3 py-1.5 text-xs text-blue-600">Entrada</td>
                          {PIEZAS.map(p => {
                            const qty = t.productos_reales_entrada?.[p.clave] || 0;
                            return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono ${qty > 0 ? 'font-bold text-blue-700' : 'text-zinc-300'}`}>{qty > 0 ? fmtQty(qty) : '--'}</td>;
                          })}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {t.salida_confirmada_at && (
                  <div className="px-3 py-1.5 border-t border-zinc-100 flex items-center gap-1 text-xs text-zinc-400">
                    <Clock className="w-3 h-3" />
                    Salida: {formatTime(t.salida_confirmada_at)}
                    {t.entrada_confirmada_at && <> · Entrada: {formatTime(t.entrada_confirmada_at)}</>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ========== POS VIEW: Salidas (Outgoing) ========== */}
        {data.salidas.length > 0 && (
          <div ref={salidasRef} className="space-y-3">
            <h2 className="text-base font-bold text-orange-700 flex items-center gap-2">
              <ArrowUpFromLine className="w-4 h-4" />
              Salidas Pendientes ({data.salidas.length})
            </h2>
            {data.salidas.map(t => (
              <TransferCard
                key={t.id}
                traslado={t}
                tipo="salida"
                onConfirm={handleConfirmarSalida}
                submitting={submitting}
                disponible={data.disponible}
              />
            ))}
          </div>
        )}

        {/* ========== POS VIEW: Entradas (Incoming) ========== */}
        {data.entradas.length > 0 && (
          <div ref={entradasRef} className="space-y-3">
            <h2 className="text-base font-bold text-blue-700 flex items-center gap-2">
              <ArrowDownToLine className="w-4 h-4" />
              Entradas Pendientes ({data.entradas.length})
            </h2>
            {data.entradas.map(t => (
              <TransferCard
                key={t.id}
                traslado={t}
                tipo="entrada"
                onConfirm={handleConfirmarEntrada}
                submitting={submitting}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {totalPending === 0 && (!isRepartidor || misTraslados.length === 0) && data.historial.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mb-4">
              <Truck className="w-8 h-8 text-zinc-400" />
            </div>
            <p className="text-zinc-500 font-medium">No hay traslados pendientes</p>
            <p className="text-zinc-400 text-sm mt-1">Los traslados programados aparecerán aquí</p>
          </div>
        )}

        {/* ========== HISTORIAL CONFIRMADOS ========== */}
        {data.historial.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-base font-bold text-zinc-500 flex items-center gap-2">
              <Check className="w-4 h-4" />
              Historial de hoy ({data.historial.length})
            </h2>
            {data.historial.map(t => (
              <div key={t.id} className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid={`historial-${t.id}`}>
                <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm font-medium text-zinc-700">{t.origen_nombre}</span>
                    <span className="text-zinc-400">→</span>
                    <span className="text-sm font-medium text-zinc-700">{t.destino_nombre}</span>
                    {t.repartidor_nombre && (
                      <span className="text-xs text-zinc-400">({t.repartidor_nombre})</span>
                    )}
                  </div>
                  {getStatusBadge(t.status)}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-zinc-50/50 text-zinc-400">
                        <th className="text-left px-3 py-1.5 font-medium text-xs w-24"></th>
                        {PIEZAS.map(p => (
                          <th key={p.clave} className="text-center px-1 py-1.5 font-medium text-xs w-10">{p.short}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Row 1: Programmed */}
                      <tr className="border-b border-zinc-100">
                        <td className="px-3 py-1.5 text-xs text-zinc-400">Prog.</td>
                        {PIEZAS.map(p => {
                          const qty = t.productos?.[p.clave] || 0;
                          return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono text-xs ${qty > 0 ? 'font-bold text-zinc-700' : 'text-zinc-300'}`}>{qty > 0 ? fmtQty(qty) : '--'}</td>;
                        })}
                      </tr>
                      {/* Row 2: Real delivered (salida) */}
                      {t.productos_reales_salida && (
                        <tr className="border-b border-zinc-100 bg-orange-50/40">
                          <td className="px-3 py-1.5 text-xs text-orange-600 font-medium">Entregado</td>
                          {PIEZAS.map(p => {
                            const qty = t.productos_reales_salida?.[p.clave] || 0;
                            const prog = t.productos?.[p.clave] || 0;
                            const diff = prog > 0 && qty !== prog;
                            return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono text-xs font-bold ${qty > 0 ? (diff ? 'text-orange-600' : 'text-orange-700') : 'text-zinc-300'}`}>{qty > 0 ? fmtQty(qty) : '--'}</td>;
                          })}
                        </tr>
                      )}
                      {/* Row 3: Real received (entrada) */}
                      {t.productos_reales_entrada && (
                        <tr className="bg-blue-50/40">
                          <td className="px-3 py-1.5 text-xs text-blue-600 font-medium">Recibido</td>
                          {PIEZAS.map(p => {
                            const qty = t.productos_reales_entrada?.[p.clave] || 0;
                            const prog = t.productos?.[p.clave] || 0;
                            const diff = prog > 0 && qty !== prog;
                            return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono text-xs font-bold ${qty > 0 ? (diff ? 'text-blue-600' : 'text-blue-700') : 'text-zinc-300'}`}>{qty > 0 ? fmtQty(qty) : '--'}</td>;
                          })}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {(t.salida_confirmada_at || t.entrada_confirmada_at || t.realizado_at) && (
                  <div className="px-3 py-1.5 border-t border-zinc-100 flex items-center gap-3 text-xs text-zinc-400">
                    <Clock className="w-3 h-3" />
                    {t.realizado_at && <>Realizado: {formatTime(t.realizado_at)}</>}
                    {t.salida_confirmada_at && <>Salida: {formatTime(t.salida_confirmada_at)}</>}
                    {t.salida_confirmada_at && t.entrada_confirmada_at && <span>·</span>}
                    {t.entrada_confirmada_at && <>Recibido: {formatTime(t.entrada_confirmada_at)}</>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
